import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { defineMessages, FormattedMessage, type IntlShape, useIntl } from 'react-intl'
import { View, type ViewStyle } from 'react-native'

import { createColumnHelper, type Row } from '@tanstack/react-table'
import { parseISO } from 'date-fns'
import styled, { useTheme } from 'styled-components/native'

import { AssignmentStatuses } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import { getCombinedAssignmentStatus } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { convertUTCToISO8601format } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/convertUTCToISO8601format'
import { type Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { AlertLoudIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AlertLoudIcon'
import { AssessmentIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AssessmentIcon'
import { AudioPauseIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AudioPauseIcon'
import { AudioPlayIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AudioPlayIcon'
import { EditIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/EditIcon'
import { InfoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIcon'
import { ProgressIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ProgressIcon'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { Tooltip, TooltipMaxWidth } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { DataTable } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/dataTable/DataTable'
import {
  type HoverActionItem,
  HoverActions,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/hoverActions/HoverActions'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { type AddedAssessmentStatusAction, AddedAssessmentStatusModal } from './AddedAssessmentStatusModal'
import { getAssessmentAccessibilityLabel, getAssessmentSubmissionDateTime } from './assessmentAttempt'
import { type ViewAnswersRequest } from './CurrentMeasuresTable'
import { TruncatedText } from './CurrentMeasuresTable.TruncatedText'
import {
  EMPTY_VALUE,
  getLatestSubmitDate,
  hasAssessmentAnswers,
  type MeasureRow,
  measuresForOutcome,
} from './getMeasureRows'
import { type MeasureDefinition, resolveMetadataToken } from './measureCatalog'
import { parseOutcomeResponseDate } from './outcomeDate'
import { type AddedAssessmentDisplayData } from './useAddedAssessmentsData'
import { useAppDispatch } from '../../../data/store'
import { setToastContent } from '../../data/ltToastAutoActions'
import {
  type FlexibleAssessment,
  type FlexibleAssessmentStatus,
  ONE_OFF_FREQUENCY,
  useUpdateFlexibleAssessmentMutation,
} from '../data/flexibleAssessmentsApi'
import { EditAssessmentModal } from '../EditAssessmentModal'

export type AddedAssessmentsProps = {
  data: readonly AddedAssessmentDisplayData[]
  canManageAssessments: boolean
  clientName?: string
  isAssignmentsLoading: boolean
  hasAssignmentsError: boolean
  nextSessionDate?: Date
  onViewAnswers?: (request: ViewAnswersRequest) => void
  onGoToChart?: (chartKey: string) => void
  onViewPendingAssessment?: () => void
  showSharedColumnHeaders?: boolean
}

type AddedAssessmentRow = Pick<
  MeasureRow,
  | 'chartKey'
  | 'description'
  | 'isPending'
  | 'measureDisplayName'
  | 'outcome'
  | 'rawScore'
  | 'responseDetailsKey'
  | 'score'
  | 'supplementalTooltip'
> & {
  /** Flexible-assessment configuration id used for status mutations and overrides. */
  id: string
  /** Concise, unique suffix used by row controls and test ids. */
  testId: string
  hasDuplicateAttempts: boolean
  frequency: string
  frequencyAccessibilityLabel: string
  frequencyDate: string
  frequencyValue: number
  status: FlexibleAssessmentStatus
  lastTakenDate?: string
  isAwaitingResponse: boolean
}

type AddedAssessmentConfigurationOverride = Pick<AddedAssessmentRow, 'frequencyDate' | 'frequencyValue' | 'status'>

type AddedAssessmentStatusModalState = {
  action: AddedAssessmentStatusAction
  rowId: string
}

type AddedAssessmentEditModalState = {
  rowId: string
  assessmentName: string
  flexibleAssessment: FlexibleAssessment
}

const MESSAGES = defineMessages({
  title: {
    defaultMessage: 'Supplemental assessments',
    description: 'Table section header for assessments that a provider added to a client’s episode',
  },
  aboutSupplementalAssessments: {
    defaultMessage: 'About supplemental assessments',
    description: 'Accessibility label for the supplemental assessments information button',
  },
  learnMoreAccessibilityLabel: {
    defaultMessage: 'Learn more about supplemental assessments',
    description: 'Accessibility label for the supplemental assessments tooltip link to the help center article',
  },
  scoreHeader: { defaultMessage: 'Score', description: 'Column header for an added assessment score' },
  descriptionHeader: {
    defaultMessage: 'Description',
    description: 'Column header for an added assessment score description',
  },
  frequencyHeader: {
    defaultMessage: 'Frequency',
    description: 'Column header for how often an added assessment is sent',
  },
  frequencyTooltip: {
    defaultMessage: 'Sent 72 hours in advance, per the selected frequency.',
    description: 'Tooltip explaining when a scheduled assessment is sent to the client',
  },
  aboutFrequency: {
    defaultMessage: 'About assessment frequency',
    description: 'Accessibility label for the frequency information button',
  },
  lastTakenHeader: {
    defaultMessage: 'Last submitted',
    description: 'Column header for the date an added assessment was last submitted',
  },
  tableAccessibilityLabel: {
    defaultMessage: 'Supplemental assessment results',
    description: 'Accessibility label for the provider-added assessment results table',
  },
  notAvailable: {
    defaultMessage: 'Not available',
    description: 'Accessibility label for an added assessment value that is not available',
  },
  pending: {
    defaultMessage: 'Pending',
    description: 'Score shown for an added assessment that has not been assigned or completed',
  },
  unknownAssessment: {
    defaultMessage: 'Unknown assessment',
    description: 'Fallback name when an added assessment’s content is no longer available',
  },
  additionalScoreDetailsTrigger: {
    defaultMessage: '{score}. Additional score details for {assessment}',
    description: 'Accessibility label for supplemental scores shown beside an added assessment score',
  },
  criticalAnswerTrigger: {
    defaultMessage: '{description}. Critical answer alert for {assessment}',
    description: 'Accessibility label for an added assessment description with a critical answer',
  },
  overallDescription: {
    defaultMessage: 'Overall - {description}',
    description: 'Summary line at the top of an added assessment’s additional details tooltip',
  },
  onceOn: {
    defaultMessage: 'Once on {date}',
    description: 'Frequency of a one-time added assessment, followed by the date it was sent',
  },
  recurringFrequency: {
    defaultMessage: 'Every {frequency, plural, one {session} other {# sessions}}',
    description: 'Frequency of a recurring added assessment, expressed as a number of sessions',
  },
  inactiveFrequency: {
    defaultMessage: 'Inactive',
    description: 'Frequency shown for a paused added assessment',
  },
  viewAnswers: {
    defaultMessage: 'View answers',
    description: 'Row action opening a client’s added assessment answers',
  },
  viewAnswersAccessibilityLabel: {
    defaultMessage: 'View answers for {assessment}',
    description: 'Accessibility label for the view answers added-assessment row action',
  },
  goToChart: { defaultMessage: 'Go to chart', description: 'Row action scrolling to an assessment’s history chart' },
  goToChartAccessibilityLabel: {
    defaultMessage: 'Go to the history chart for {assessment}',
    description: 'Accessibility label for the go to chart added-assessment row action',
  },
  pause: { defaultMessage: 'Pause', description: 'Row action pausing a recurring added assessment' },
  pauseAccessibilityLabel: {
    defaultMessage: 'Pause {assessment}',
    description: 'Accessibility label for the pause added-assessment row action',
  },
  resume: { defaultMessage: 'Resume', description: 'Row action resuming a recurring added assessment' },
  resumeAccessibilityLabel: {
    defaultMessage: 'Resume {assessment}',
    description: 'Accessibility label for the resume added-assessment row action',
  },
  edit: { defaultMessage: 'Edit', description: 'Row action editing an added assessment' },
  editAccessibilityLabel: {
    defaultMessage: 'Edit {assessment}',
    description: 'Accessibility label for the edit added-assessment row action',
  },
  assessmentPaused: {
    defaultMessage: '{assessment} paused',
    description: 'Success toast after pausing a recurring added assessment',
  },
  assessmentResumed: {
    defaultMessage: '{assessment} resumed',
    description: 'Success toast after resuming a recurring added assessment',
  },
  assessmentPauseFailed: {
    defaultMessage: "{assessment} couldn't be paused. Please try again.",
    description: 'Error toast after pausing a recurring added assessment fails',
  },
  assessmentResumeFailed: {
    defaultMessage: "{assessment} couldn't be resumed. Please try again.",
    description: 'Error toast after resuming a recurring added assessment fails',
  },
})

const SUPPLEMENTAL_ASSESSMENTS_URL =
  'https://provider-support.lyrahealth.com/hc/en-us/articles/55636296139795-Client-Optional-Supplemental-Assessment-Measures'

const COLUMN_WIDTH = {
  assessment: { preferred: 478, min: 80 },
  score: { preferred: 120, min: 60 },
  description: { preferred: 180, min: 60 },
  frequency: { preferred: 220, min: 100 },
  lastTakenDate: { preferred: 140, min: 80 },
} as const

const COLUMN_CONTENT_INSET = 16

const ScoreCellContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flex: 1,
  minWidth: 0,
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['4px'],
  overflow: 'hidden',
}))

const DescriptionCellContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flex: 1,
  minWidth: 0,
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['4px'],
  overflow: 'hidden',
}))

const AdditionalDetailsTooltipContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['12px'],
}))

const AdditionalDetailsTooltipItem = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['4px'],
}))

const HeaderCellContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flex: 1,
  minWidth: 0,
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
  overflow: 'hidden',
}))

const AdditionalDetailsIconContainer = styled.View({
  flexShrink: 0,
})

const VisuallyHiddenHeaderText = styled(BodyText)({
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  opacity: 0,
})

const AddedAssessmentHoverActions = styled(HoverActions)<{ isVisible: boolean }>(({ isVisible }) => ({
  opacity: isVisible ? 1 : 0,
  pointerEvents: isVisible ? 'auto' : 'none',
}))

const isCompletedAssignment = (assignment: Assignment) =>
  getCombinedAssignmentStatus({ assignment }) === AssignmentStatuses.completed

const getLatestAssignment = (assignments: readonly Assignment[]) =>
  assignments.reduce<Assignment | undefined>((latestAssignment, assignment) => {
    if (!latestAssignment) return assignment

    const assignmentTime = parseISO(assignment.create_date).getTime()
    const latestAssignmentTime = parseISO(latestAssignment.create_date).getTime()
    if (!Number.isFinite(assignmentTime)) return latestAssignment
    return !Number.isFinite(latestAssignmentTime) || assignmentTime > latestAssignmentTime
      ? assignment
      : latestAssignment
  }, undefined)

const getAssessmentMeasures = (assessment: AddedAssessmentDisplayData, displayAssignment?: Assignment) => {
  const measureTokens =
    displayAssignment?.content_meta_data?.clinicalMeasuresIncluded ??
    assessment.contentMetadata?.meta_data?.clinicalMeasuresIncluded ??
    displayAssignment?.content.meta_data?.clinicalMeasuresIncluded

  const resolvedMeasures = (measureTokens ?? []).map(resolveMetadataToken)
  if (resolvedMeasures.some((measures) => measures.length === 0)) return []

  const measuresById = new Map(
    resolvedMeasures.flatMap((measures) => measures.map((measure) => [measure.id, measure] as const)),
  )
  return [...measuresById.values()]
}

const formatFrequency = (
  frequency: number,
  createDate: string,
  intl: IntlShape,
): Pick<AddedAssessmentRow, 'frequency' | 'frequencyAccessibilityLabel'> => {
  const notAvailable = intl.formatMessage(MESSAGES.notAvailable)

  if (!Number.isInteger(frequency) || frequency < ONE_OFF_FREQUENCY) {
    return { frequency: EMPTY_VALUE, frequencyAccessibilityLabel: notAvailable }
  }

  if (frequency > ONE_OFF_FREQUENCY) {
    const frequencyText = intl.formatMessage(MESSAGES.recurringFrequency, { frequency })
    return { frequency: frequencyText, frequencyAccessibilityLabel: frequencyText }
  }

  const parsedCreateDate = parseOutcomeResponseDate(createDate)
  if (!parsedCreateDate) {
    return { frequency: EMPTY_VALUE, frequencyAccessibilityLabel: notAvailable }
  }

  return {
    frequency: intl.formatMessage(MESSAGES.onceOn, {
      date: intl.formatDate(parsedCreateDate, { month: 'numeric', day: 'numeric', year: 'numeric' }),
    }),
    frequencyAccessibilityLabel: intl.formatMessage(MESSAGES.onceOn, {
      date: intl.formatDate(parsedCreateDate, { month: 'long', day: 'numeric', year: 'numeric' }),
    }),
  }
}

/**
 * Converts one date-transformed assessment into the flat presentation model used by DataTable.
 * Row existence never depends on a linked outcome or a numeric score.
 */
export const renderRow = (
  assessment: AddedAssessmentDisplayData,
  intl: IntlShape,
  displayAssignment: Assignment | undefined,
  measure: MeasureDefinition | undefined,
  testId: string,
  measureDisplayName: string,
): AddedAssessmentRow => {
  const resultRow =
    measure && assessment.selectedResult
      ? measuresForOutcome(assessment.selectedResult.outcome, [], {}, intl).find(({ id }) => id === measure.id)
      : undefined
  const { create_date: createDate, frequency, update_date: updateDate } = assessment.flexibleAssessment
  const frequencyDate = frequency === ONE_OFF_FREQUENCY ? updateDate : createDate
  const formattedFrequency = formatFrequency(frequency, frequencyDate, intl)
  // An incomplete assignment has no reliable appointment/occurrence date in the current payload.
  // Only infer Pending in Last known; a selected-date placeholder must remain unavailable.
  const isPending =
    assessment.isLatestView &&
    !assessment.selectedResult &&
    (!displayAssignment || !isCompletedAssignment(displayAssignment))
  const hasDisplayedScore = !isPending && typeof resultRow?.rawScore === 'number' && Number.isFinite(resultRow.rawScore)
  const isAwaitingResponse = !assessment.selectedResult && (isPending || !assessment.isLatestView)

  return {
    id: assessment.id,
    testId,
    hasDuplicateAttempts: assessment.hasDuplicateAttempts,
    measureDisplayName,
    score: isPending ? intl.formatMessage(MESSAGES.pending) : (resultRow?.score ?? EMPTY_VALUE),
    description: resultRow?.description ?? EMPTY_VALUE,
    supplementalTooltip: resultRow?.supplementalTooltip,
    ...formattedFrequency,
    frequencyDate,
    frequencyValue: frequency,
    status: assessment.flexibleAssessment.status,
    isPending,
    isAwaitingResponse,
    // Actions must describe the score currently on screen. A linked outcome alone is not enough:
    // completed-but-unscored and pending rows deliberately render without score actions.
    outcome: hasDisplayedScore ? resultRow?.outcome : undefined,
    rawScore: hasDisplayedScore ? resultRow?.rawScore : undefined,
    responseDetailsKey: hasDisplayedScore ? resultRow?.responseDetailsKey : undefined,
    chartKey: hasDisplayedScore ? resultRow?.chartKey : undefined,
    // Completion and scoring are independent. An unscored submission still has a submit date.
    lastTakenDate: assessment.selectedResult
      ? (getLatestSubmitDate(assessment.selectedResult.assignment) ?? assessment.selectedResult.outcome.response_date)
      : undefined,
  }
}

export const renderRows = (assessment: AddedAssessmentDisplayData, intl: IntlShape): AddedAssessmentRow[] => {
  const displayAssignment = assessment.selectedResult?.assignment ?? getLatestAssignment(assessment.assignments)
  const measures = getAssessmentMeasures(assessment, displayAssignment)
  if (measures.length > 1) {
    return measures.map((measure) =>
      renderRow(
        assessment,
        intl,
        displayAssignment,
        measure,
        `${assessment.testId}-${measure.id}`,
        intl.formatMessage(measure.name),
      ),
    )
  }

  const measureDisplayName =
    displayAssignment?.content.title ||
    assessment.contentMetadata?.title ||
    displayAssignment?.content_meta_data?.title ||
    displayAssignment?.title ||
    intl.formatMessage(MESSAGES.unknownAssessment)
  return [renderRow(assessment, intl, displayAssignment, measures[0], assessment.testId, measureDisplayName)]
}

const AdditionalDetailsTooltip: React.FC<{
  rowId: string
  description?: string
  details: NonNullable<AddedAssessmentRow['supplementalTooltip']>['details']
}> = ({ rowId, description, details }) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const textColor = colors.components?.tooltip?.text ?? colors.textInverse

  return (
    <AdditionalDetailsTooltipContainer>
      {description && (
        <View accessible={false} aria-hidden importantForAccessibility='no-hide-descendants'>
          <BodyText
            text={formatMessage(MESSAGES.overallDescription, { description })}
            size={BodyTextSize.SMALL}
            color={textColor}
          />
        </View>
      )}
      {details.map(({ id, label, value }) => (
        <AdditionalDetailsTooltipItem key={id} testID={tID(`AddedAssessments-${rowId}-detail-${id}`)}>
          <BodyText text={label} size={BodyTextSize.SMALL} color={textColor} medium wrap />
          <BodyText text={value} size={BodyTextSize.SMALL} color={textColor} wrap />
        </AdditionalDetailsTooltipItem>
      ))}
    </AdditionalDetailsTooltipContainer>
  )
}

const columnHelper = createColumnHelper<AddedAssessmentRow>()
const getAddedAssessmentRowId = (row: AddedAssessmentRow) => row.testId

const getColumnLayout = (column: keyof typeof COLUMN_WIDTH, flex = 0) => ({
  size: COLUMN_WIDTH[column].preferred,
  minSize: COLUMN_WIDTH[column].min,
  meta: {
    flex,
    inlineComponent: true,
    inlineHeaderComponent: true,
    shrinkToFit: true,
    ...(flex ? {} : { contentPaddingLeft: COLUMN_CONTENT_INSET }),
  },
})

export const AddedAssessments: React.FC<AddedAssessmentsProps> = ({
  data,
  canManageAssessments,
  clientName,
  isAssignmentsLoading,
  hasAssignmentsError,
  nextSessionDate,
  onViewAnswers,
  onGoToChart,
  onViewPendingAssessment,
  showSharedColumnHeaders = false,
}) => {
  const intl = useIntl()
  const { formatDate, formatMessage } = intl
  const { colors } = useTheme()
  const dispatch = useAppDispatch()
  const [updateFlexibleAssessmentStatus, { isLoading: isUpdatingStatus }] = useUpdateFlexibleAssessmentMutation()
  const statusUpdateInFlight = useRef<string | null>(null)
  const [configurationOverrides, setConfigurationOverrides] = useState<
    Record<string, AddedAssessmentConfigurationOverride>
  >({})
  const [statusModal, setStatusModal] = useState<AddedAssessmentStatusModalState | null>(null)
  const [editModal, setEditModal] = useState<AddedAssessmentEditModalState | null>(null)
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)
  const [focusedActionRowId, setFocusedActionRowId] = useState<string | null>(null)
  const [tableWidth, setTableWidth] = useState(0)
  const shouldShowSection = data.some(({ isLatestView, selectedResult }) => isLatestView || Boolean(selectedResult))
  const rows = useMemo(() => data.flatMap((assessment) => renderRows(assessment, intl)), [data, intl])

  const editModalAssignments = useMemo(
    () => (editModal ? (data.find(({ id }) => id === editModal.rowId)?.assignments ?? []) : []),
    [data, editModal],
  )
  const statusModalRow = useMemo(() => {
    if (!statusModal) return undefined

    const row = rows.find(({ id }) => id === statusModal.rowId)
    if (!row) return undefined

    const configurationOverride = configurationOverrides[row.id]
    return {
      ...row,
      status: configurationOverride?.status ?? row.status,
      frequencyValue: configurationOverride?.frequencyValue ?? row.frequencyValue,
    }
  }, [configurationOverrides, rows, statusModal])

  useEffect(() => {
    setConfigurationOverrides((currentOverrides) => {
      const nextOverrides = Object.fromEntries(
        Object.entries(currentOverrides).filter(([id, override]) => {
          const row = rows.find((candidate) => candidate.id === id)
          return row !== undefined && (row.status !== override.status || row.frequencyValue !== override.frequencyValue)
        }),
      )

      return Object.keys(nextOverrides).length === Object.keys(currentOverrides).length
        ? currentOverrides
        : nextOverrides
    })
  }, [configurationOverrides, rows])

  useEffect(() => {
    if (!statusModal) return

    const expectedStatus = statusModal.action === 'pause' ? 'ACTIVE' : 'PAUSED'
    if (!statusModalRow || statusModalRow.status !== expectedStatus) setStatusModal(null)
  }, [statusModal, statusModalRow])

  const updateStatus = useCallback(
    async (row: AddedAssessmentRow, status: FlexibleAssessmentStatus, frequency = row.frequencyValue) => {
      const isRecurring = Number.isInteger(row.frequencyValue) && row.frequencyValue > ONE_OFF_FREQUENCY
      const isValidTransition =
        (row.status === 'ACTIVE' && status === 'PAUSED') || (row.status === 'PAUSED' && status === 'ACTIVE')
      const isValidFrequency = Number.isInteger(frequency) && frequency >= ONE_OFF_FREQUENCY
      if (!isRecurring || !isValidTransition || !isValidFrequency || statusUpdateInFlight.current !== null) return false

      statusUpdateInFlight.current = row.id
      const isPausing = status === 'PAUSED'

      try {
        const updatedAssessment = await updateFlexibleAssessmentStatus({
          id: row.id,
          status,
          previousFrequency: row.frequencyValue,
          previousStatus: row.status,
          ...(isPausing ? {} : { frequency }),
        }).unwrap()
        // The invalidated list refetch begins after the PATCH resolves. Reflect the authoritative
        // configuration immediately so the stale action cannot submit the same transition in that gap.
        setConfigurationOverrides((currentOverrides) => ({
          ...currentOverrides,
          [row.id]: {
            frequencyDate: frequency === ONE_OFF_FREQUENCY ? updatedAssessment.update_date : row.frequencyDate,
            frequencyValue: frequency,
            status,
          },
        }))
        dispatch(
          setToastContent({
            text: formatMessage(isPausing ? MESSAGES.assessmentPaused : MESSAGES.assessmentResumed, {
              assessment: row.measureDisplayName,
            }),
            id: 'AddedAssessments-statusUpdate-success',
            toastType: 'success',
          }),
        )
      } catch {
        dispatch(
          setToastContent({
            text: formatMessage(isPausing ? MESSAGES.assessmentPauseFailed : MESSAGES.assessmentResumeFailed, {
              assessment: row.measureDisplayName,
            }),
            id: 'AddedAssessments-statusUpdate-error',
            toastType: 'error',
          }),
        )
      } finally {
        statusUpdateInFlight.current = null
      }

      return true
    },
    [dispatch, formatMessage, updateFlexibleAssessmentStatus],
  )

  const closeStatusModal = useCallback(() => {
    if (!isUpdatingStatus && statusUpdateInFlight.current === null) setStatusModal(null)
  }, [isUpdatingStatus])

  const confirmStatusUpdate = useCallback(
    async (frequency?: number) => {
      if (!statusModal || !statusModalRow) return

      const nextStatus = statusModal.action === 'pause' ? 'PAUSED' : 'ACTIVE'
      const didUpdate = await updateStatus(statusModalRow, nextStatus, frequency ?? statusModalRow.frequencyValue)
      if (didUpdate) setStatusModal(null)
    },
    [statusModal, statusModalRow, updateStatus],
  )

  const openRowDetails = useCallback(
    (assessmentRow: AddedAssessmentRow) => {
      const { chartKey, isAwaitingResponse, measureDisplayName, outcome, rawScore, responseDetailsKey } = assessmentRow

      if (isAwaitingResponse) {
        onViewPendingAssessment?.()
        return
      }

      if (!responseDetailsKey || !hasAssessmentAnswers(assessmentRow)) return

      const formatModalDate = (date?: string) =>
        date ? formatDate(convertUTCToISO8601format(date), { month: '2-digit', day: '2-digit', year: 'numeric' }) : ''
      const shouldHideAnswerDescription = !chartKey && responseDetailsKey !== 'RCADS-TOTAL'

      onViewAnswers?.({
        clientName: clientName ?? '',
        title: measureDisplayName,
        assessmentType: chartKey ?? responseDetailsKey,
        score: rawScore,
        answerDescription: shouldHideAnswerDescription ? null : undefined,
        dateStarted: formatModalDate(outcome?.date_created),
        dateSubmitted: formatModalDate(outcome?.response_date),
        answers: outcome?.response_details?.filter(({ groups }) => groups.includes(responseDetailsKey)),
      })
    },
    [clientName, formatDate, onViewAnswers, onViewPendingAssessment],
  )

  const handleRowPress = useCallback((row: Row<AddedAssessmentRow>) => openRowDetails(row.original), [openRowDetails])

  const canOpenRowDetails = Boolean(onViewAnswers || onViewPendingAssessment)

  const renderRowActions = useMemo(() => {
    return (row: Row<AddedAssessmentRow>, isHovered: boolean) => {
      const { original } = row
      const {
        chartKey,
        frequencyValue,
        id,
        isAwaitingResponse,
        isPending,
        measureDisplayName,
        outcome,
        rawScore,
        status: serverStatus,
        testId,
      } = original
      const configurationOverride = configurationOverrides[id]
      const status = configurationOverride?.status ?? serverStatus
      const effectiveFrequency = configurationOverride?.frequencyValue ?? frequencyValue
      const hasDisplayedScore = !isPending && typeof rawScore === 'number' && Number.isFinite(rawScore)
      const canViewAnswers = Boolean(hasDisplayedScore && onViewAnswers && hasAssessmentAnswers(original))
      const canGoToChart = Boolean(hasDisplayedScore && onGoToChart && chartKey && outcome)
      const shouldOpenZeroState = Boolean(onViewPendingAssessment && isAwaitingResponse)
      const canUpdateStatus =
        canManageAssessments &&
        Number.isInteger(effectiveFrequency) &&
        effectiveFrequency > ONE_OFF_FREQUENCY &&
        (status === 'ACTIVE' || status === 'PAUSED')
      const canEdit = canManageAssessments && status === 'ACTIVE'

      if (!shouldOpenZeroState && !canViewAnswers && !canGoToChart && !canUpdateStatus && !canEdit) return null

      const assessmentAccessibilityLabel = getAssessmentAccessibilityLabel(original, intl)
      const items: HoverActionItem[] = []

      if (shouldOpenZeroState || canGoToChart) {
        items.push({
          id: 'go-to-chart',
          icon: <ProgressIcon size={24} fillColor={colors.iconDefault} />,
          label: formatMessage(MESSAGES.goToChart),
          accessibilityLabel: formatMessage(MESSAGES.goToChartAccessibilityLabel, {
            assessment: assessmentAccessibilityLabel,
          }),
          testId: tID(`AddedAssessments-${testId}-goToChart`),
          onPress: shouldOpenZeroState ? () => onViewPendingAssessment?.() : () => onGoToChart?.(chartKey as string),
        })
      }

      if (shouldOpenZeroState || canViewAnswers) {
        items.push({
          id: 'view-answers',
          icon: <AssessmentIcon size={24} color={colors.iconDefault} />,
          label: formatMessage(MESSAGES.viewAnswers),
          accessibilityLabel: formatMessage(MESSAGES.viewAnswersAccessibilityLabel, {
            assessment: assessmentAccessibilityLabel,
          }),
          testId: tID(`AddedAssessments-${testId}-viewAnswers`),
          onPress: () => openRowDetails(original),
        })
      }

      if (canEdit) {
        items.push({
          id: 'edit',
          icon: <EditIcon size={24} isFilled={false} fillColor={colors.iconDefault} />,
          label: formatMessage(MESSAGES.edit),
          accessibilityLabel: formatMessage(MESSAGES.editAccessibilityLabel, {
            assessment: assessmentAccessibilityLabel,
          }),
          testId: tID(`AddedAssessments-${testId}-edit`),
          onPress: () => {
            const selectedAssessment = data.find(({ id: assessmentId }) => assessmentId === id)
            if (!selectedAssessment) return

            setEditModal({
              rowId: id,
              assessmentName: measureDisplayName,
              flexibleAssessment: {
                ...selectedAssessment.flexibleAssessment,
                frequency: effectiveFrequency,
                status,
              },
            })
          },
        })
      }

      if (canUpdateStatus) {
        const isPaused = status === 'PAUSED'
        items.push({
          // Keep the key stable across the refetch so keyboard focus survives Pause -> Resume.
          id: 'toggle-status',
          icon: isPaused ? (
            <AudioPlayIcon width={16} height={18} fillColor={colors.iconDefault} />
          ) : (
            <AudioPauseIcon width={13} height={18} fillColor={colors.iconDefault} />
          ),
          label: formatMessage(isPaused ? MESSAGES.resume : MESSAGES.pause),
          accessibilityLabel: formatMessage(
            isPaused ? MESSAGES.resumeAccessibilityLabel : MESSAGES.pauseAccessibilityLabel,
            { assessment: assessmentAccessibilityLabel },
          ),
          testId: tID(`AddedAssessments-${testId}-${isPaused ? 'resume' : 'pause'}`),
          onPress: () =>
            setStatusModal({
              action: isPaused ? 'resume' : 'pause',
              rowId: original.id,
            }),
        })
      }

      return (
        <AddedAssessmentHoverActions
          items={items}
          isVisible={isHovered || focusedActionRowId === row.id}
          onFocus={() => setFocusedActionRowId(row.id)}
          onHover={() => setHoveredRowId(row.id)}
          onBlur={() => setFocusedActionRowId(null)}
        />
      )
    }
  }, [
    canManageAssessments,
    colors,
    configurationOverrides,
    data,
    focusedActionRowId,
    formatMessage,
    intl,
    onGoToChart,
    onViewAnswers,
    onViewPendingAssessment,
    openRowDetails,
  ])

  const columns = useMemo(() => {
    const header = (text: string, testID: string, fitContent = false) => (
      <TruncatedText header fitContent={fitContent} resizeKey={tableWidth} testID={testID} text={text} />
    )
    const visuallyHiddenHeader = (text: string, testID: string) => (
      <VisuallyHiddenHeaderText testID={tID(testID)} text={text} />
    )
    const sharedHeader = showSharedColumnHeaders ? header : visuallyHiddenHeader
    const headerInfo = (id: string, accessibilityLabel: string, content: React.ReactNode) => (
      <Tooltip
        testID={tID(`AddedAssessments-${id}-info`)}
        accessibilityLabel={accessibilityLabel}
        hoverEnabled
        openOnPress
        placement='top'
        content={content}
        wrapperStyle={{ flexGrow: 0, flexShrink: 0 }}
      >
        <InfoIcon size={16} fillColor={colors.iconDefault} testID={tID(`AddedAssessments-${id}-infoIcon`)} />
      </Tooltip>
    )
    const tooltipTextColor = colors.components?.tooltip?.text ?? colors.textInverse

    return [
      columnHelper.accessor('measureDisplayName', {
        id: 'name',
        header: () => (
          <HeaderCellContainer>
            {header(formatMessage(MESSAGES.title), 'AddedAssessments-assessmentHeader', true)}
            {headerInfo(
              'supplementalAssessments',
              formatMessage(MESSAGES.aboutSupplementalAssessments),
              <BodyText
                size={BodyTextSize.SMALL}
                color={tooltipTextColor}
                text={
                  <FormattedMessage
                    defaultMessage='Assigned and managed manually by the provider on a one-time or recurring schedule. <link>Learn more</link>'
                    description='Tooltip explaining supplemental assessments, with a link to the help center article'
                    values={{
                      link: (chunks: string) => (
                        <Link
                          text={chunks}
                          testID={tID('AddedAssessments-supplementalAssessments-learnMore')}
                          accessibilityLabel={formatMessage(MESSAGES.learnMoreAccessibilityLabel)}
                          size={BodyTextSize.SMALL}
                          color={tooltipTextColor}
                          onPress={() => window.open(SUPPLEMENTAL_ASSESSMENTS_URL, '_blank')}
                          underline
                        />
                      ),
                    }}
                  />
                }
              />,
            )}
          </HeaderCellContainer>
        ),
        ...getColumnLayout('assessment', 1),
        cell: ({ row }) => (
          <TruncatedText
            accessibilityLabel={getAssessmentAccessibilityLabel(row.original, intl)}
            resizeKey={tableWidth}
            testID={`AddedAssessments-${row.original.testId}-name`}
            text={row.original.measureDisplayName}
          />
        ),
      }),
      columnHelper.accessor('score', {
        header: () => sharedHeader(formatMessage(MESSAGES.scoreHeader), 'AddedAssessments-scoreHeader'),
        ...getColumnLayout('score'),
        cell: ({ row }) => {
          const { score, supplementalTooltip, testId } = row.original
          const scoreDetails = supplementalTooltip?.hasCriticalDetails ? undefined : supplementalTooltip
          const accessibleScore = score === EMPTY_VALUE ? formatMessage(MESSAGES.notAvailable) : score
          const assessmentAccessibilityLabel = getAssessmentAccessibilityLabel(row.original, intl)
          const scoreContent = (
            <TruncatedText
              accessibilityLabel={accessibleScore}
              disableTooltip={score === EMPTY_VALUE}
              fitContent={Boolean(scoreDetails)}
              resizeKey={tableWidth}
              testID={`AddedAssessments-${testId}-score`}
              text={score}
            />
          )

          if (!scoreDetails) return scoreContent

          return (
            <ScoreCellContainer>
              {scoreContent}
              <Tooltip
                testID={`AddedAssessments-${testId}-score-details`}
                content={<AdditionalDetailsTooltip rowId={testId} details={scoreDetails.details} />}
                accessibilityLabel={formatMessage(MESSAGES.additionalScoreDetailsTrigger, {
                  score: accessibleScore,
                  assessment: assessmentAccessibilityLabel,
                })}
                hoverEnabled
                openOnPress
                placement='top'
                tooltipSize={TooltipMaxWidth.WIDE}
                wrapperStyle={{ flexGrow: 0, flexShrink: 0 }}
              >
                <InfoIcon
                  size={16}
                  fillColor={colors.iconDefault}
                  testID={tID(`AddedAssessments-${testId}-additionalInfo`)}
                />
              </Tooltip>
            </ScoreCellContainer>
          )
        },
      }),
      columnHelper.accessor('description', {
        header: () => sharedHeader(formatMessage(MESSAGES.descriptionHeader), 'AddedAssessments-descriptionHeader'),
        ...getColumnLayout('description'),
        cell: ({ row }) => {
          const { description, supplementalTooltip, testId } = row.original
          const criticalDetails = supplementalTooltip?.hasCriticalDetails ? supplementalTooltip : undefined
          const accessibleDescription = description === EMPTY_VALUE ? formatMessage(MESSAGES.notAvailable) : description
          const assessmentAccessibilityLabel = getAssessmentAccessibilityLabel(row.original, intl)
          const descriptionContent = (
            <DescriptionCellContainer>
              <TruncatedText
                accessibilityLabel={accessibleDescription}
                disableTooltip={Boolean(criticalDetails) || description === EMPTY_VALUE}
                fitContent
                resizeKey={tableWidth}
                testID={`AddedAssessments-${testId}-description`}
                text={description}
              />
              {criticalDetails ? (
                <AdditionalDetailsIconContainer
                  accessible={false}
                  aria-hidden
                  importantForAccessibility='no-hide-descendants'
                >
                  <AlertLoudIcon
                    fillColor={colors.iconWarning}
                    testID={tID(`AddedAssessments-${testId}-criticalAlert`)}
                  />
                </AdditionalDetailsIconContainer>
              ) : null}
            </DescriptionCellContainer>
          )

          if (!criticalDetails) return descriptionContent

          return (
            <Tooltip
              testID={`AddedAssessments-${testId}-description-details`}
              content={
                <AdditionalDetailsTooltip
                  rowId={testId}
                  description={description === EMPTY_VALUE ? undefined : description}
                  details={criticalDetails.details}
                />
              }
              accessibilityLabel={formatMessage(MESSAGES.criticalAnswerTrigger, {
                description: accessibleDescription,
                assessment: assessmentAccessibilityLabel,
              })}
              hoverEnabled
              openOnPress
              placement='top'
              tooltipSize={TooltipMaxWidth.WIDE}
              wrapperStyle={{ flexGrow: 1, flexShrink: 1, maxWidth: '100%', minWidth: 0, display: 'flex' }}
              style={
                {
                  flexGrow: 1,
                  flexShrink: 1,
                  maxWidth: '100%',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                } as ViewStyle
              }
            >
              {descriptionContent}
            </Tooltip>
          )
        },
      }),
      columnHelper.accessor('frequency', {
        header: () =>
          showSharedColumnHeaders ? (
            <HeaderCellContainer>
              {header(formatMessage(MESSAGES.frequencyHeader), 'AddedAssessments-frequencyHeader')}
              {headerInfo(
                'frequency',
                formatMessage(MESSAGES.aboutFrequency),
                formatMessage(MESSAGES.frequencyTooltip),
              )}
            </HeaderCellContainer>
          ) : (
            visuallyHiddenHeader(formatMessage(MESSAGES.frequencyHeader), 'AddedAssessments-frequencyHeader')
          ),
        ...getColumnLayout('frequency'),
        cell: ({ row }) => {
          const {
            frequency,
            frequencyAccessibilityLabel,
            frequencyDate,
            id,
            status: serverStatus,
            testId,
          } = row.original
          const configurationOverride = configurationOverrides[id]
          const isPaused = (configurationOverride?.status ?? serverStatus) === 'PAUSED'
          const overriddenFrequency = configurationOverride?.frequencyValue
          const formattedOverride =
            overriddenFrequency !== undefined
              ? formatFrequency(overriddenFrequency, configurationOverride?.frequencyDate ?? frequencyDate, intl)
              : undefined
          const inactiveFrequency = formatMessage(MESSAGES.inactiveFrequency)
          const displayedFrequency = isPaused ? inactiveFrequency : (formattedOverride?.frequency ?? frequency)
          const displayedFrequencyAccessibilityLabel = isPaused
            ? inactiveFrequency
            : (formattedOverride?.frequencyAccessibilityLabel ?? frequencyAccessibilityLabel)

          return (
            <TruncatedText
              accessibilityLabel={displayedFrequencyAccessibilityLabel}
              color={isPaused ? colors.textSecondary : undefined}
              resizeKey={tableWidth}
              testID={`AddedAssessments-${testId}-frequency`}
              text={displayedFrequency}
            />
          )
        },
      }),
      columnHelper.accessor('lastTakenDate', {
        header: () => sharedHeader(formatMessage(MESSAGES.lastTakenHeader), 'AddedAssessments-lastTakenHeader'),
        ...getColumnLayout('lastTakenDate'),
        cell: ({ row }) => {
          const { hasDuplicateAttempts, lastTakenDate, testId } = row.original
          const date = parseOutcomeResponseDate(lastTakenDate)
          if (!date) {
            return (
              <TruncatedText
                accessibilityLabel={formatMessage(MESSAGES.notAvailable)}
                disableTooltip
                resizeKey={tableWidth}
                testID={`AddedAssessments-${testId}-lastTakenDate`}
                text={EMPTY_VALUE}
              />
            )
          }

          const submittedAt = hasDuplicateAttempts ? getAssessmentSubmissionDateTime(lastTakenDate, intl) : undefined

          return (
            <TruncatedText
              accessibilityLabel={submittedAt ?? formatDate(date, { month: 'long', day: 'numeric', year: 'numeric' })}
              resizeKey={tableWidth}
              testID={`AddedAssessments-${testId}-lastTakenDate`}
              text={formatDate(date, { month: 'numeric', day: 'numeric', year: 'numeric' })}
              tooltipContent={submittedAt}
            />
          )
        },
      }),
    ]
  }, [colors, configurationOverrides, formatDate, formatMessage, intl, showSharedColumnHeaders, tableWidth])

  if (!shouldShowSection) return null

  return (
    <View testID={tID('AddedAssessments')}>
      <View
        testID={tID('AddedAssessments-table')}
        onLayout={({ nativeEvent }) => setTableWidth(nativeEvent.layout.width)}
      >
        <DataTable
          accessibilityLabel={formatMessage(MESSAGES.tableAccessibilityLabel)}
          columns={columns}
          data={rows}
          getRowId={getAddedAssessmentRowId}
          cellAlignment='left'
          emptyTableText=''
          rowHeaderColumn='name'
          striped
          rowHeight={42}
          hoveredRowId={hoveredRowId}
          onRowHover={setHoveredRowId}
          onRowClick={canOpenRowDetails ? handleRowPress : undefined}
          rowActions={renderRowActions}
          tableContainerStyle={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, paddingBottom: 4 }}
          withTableSemantics
        />
      </View>
      {statusModal && statusModalRow && (
        <AddedAssessmentStatusModal
          action={statusModal.action}
          assessmentName={statusModalRow.measureDisplayName}
          clientName={clientName}
          frequency={statusModalRow.frequencyValue}
          isLoading={isUpdatingStatus}
          onConfirm={(frequency) => void confirmStatusUpdate(frequency)}
          onRequestClose={closeStatusModal}
          visible
        />
      )}
      {editModal && (
        <EditAssessmentModal
          assessmentName={editModal.assessmentName}
          assignments={editModalAssignments}
          flexibleAssessment={editModal.flexibleAssessment}
          isAssignmentsLoading={isAssignmentsLoading}
          hasAssignmentsError={hasAssignmentsError}
          nextSessionDate={nextSessionDate}
          onRequestClose={() => setEditModal(null)}
          visible
        />
      )}
    </View>
  )
}
